import {
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { EncryptionService } from '../../services/encryption.service';
import { ServerClockService } from '../../services/server-clock.service';
import { environment } from '../../../environments/environment';
import { EMPTY, from, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import {
  buildHmacHeaders,
  isTimestampDriftError,
  shouldAttachHmac,
} from '../helpers/hmac.helper';

const pendingRequests = new Set<string>();

function withHmacHeaders(
  headers: HttpHeaders,
  req: HttpRequest<unknown>,
  timestamp: string
): HttpHeaders {
  const hmacHeaders = buildHmacHeaders({
    method: req.method,
    url: req.urlWithParams,
    body: req.body,
    secret: environment.hmacSecretKey,
    timestamp,
  });

  return headers
    .set('x-signature', hmacHeaders['x-signature'])
    .set('x-timestamp', hmacHeaders['x-timestamp']);
}

export const setInterceptor: HttpInterceptorFn = (req, next) => {
  const encryptionService = inject(EncryptionService);
  const serverClock = inject(ServerClockService);
  const platformId = inject(PLATFORM_ID);

  const isBrowser = isPlatformBrowser(platformId);
  if (!isBrowser) {
    return next(req);
  }

  const token =
    encryptionService.decryptLocalStorage(environment.storageKey) ?? '';
  const fcm = encryptionService.decryptLocalStorage('fcmToken') ?? '';
  const language = localStorage['language'] ?? 'en';
  const timezone = serverClock.timezone();

  const headersObj: Record<string, string> = {
    'x-device-type': 'WEB',
    'x-device-token': fcm,
    'Accept-Language': language,
    'x-portal': 'SERVICE_PROVIDER',
    'cache-control': 'no-cache, max-age=0',
    'ngsw-cache': 'false',
    Pragma: 'no-cache',
    'x-timezone': timezone,
  };

  if (token) {
    headersObj['authorization'] = `Bearer ${token}`;
  }

  let headers = req.headers;
  Object.entries(headersObj).forEach(([k, v]) => {
    headers = headers.set(k, v);
  });

  if (!(req.body instanceof FormData)) {
    headers = headers.set('Content-Type', 'application/json');
  }

  const attachHmac =
    !!environment.hmacSecretKey &&
    shouldAttachHmac(req.urlWithParams, environment.base_url);

  if (attachHmac) {
    headers = withHmacHeaders(headers, req, serverClock.nowMs().toString());
  }

  const cloned = req.clone({ headers });

  const dispatch = (request: HttpRequest<unknown>, allowRetry: boolean) =>
    next(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!allowRetry || !attachHmac || !isTimestampDriftError(error)) {
          return throwError(() => error);
        }

        return from(serverClock.sync()).pipe(
          switchMap(() => {
            const retried = request.clone({
              headers: withHmacHeaders(
                request.headers.set('x-timezone', serverClock.timezone()),
                request,
                serverClock.nowMs().toString()
              ),
            });
            return dispatch(retried, false);
          })
        );
      })
    );

  const isMutatingMethod = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(
    req.method
  );

  if (isMutatingMethod) {
    const requestKey = `${req.method}:${req.urlWithParams}:${
      req.body instanceof FormData ? 'form-data' : JSON.stringify(req.body)
    }`;

    if (pendingRequests.has(requestKey)) {
      return EMPTY;
    }

    pendingRequests.add(requestKey);

    return dispatch(cloned, true).pipe(
      finalize(() => {
        pendingRequests.delete(requestKey);
      })
    );
  }

  return dispatch(cloned, true);
};
